import React, { useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  Stack,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { convertUnderscoreToSpaces, getStatusColor } from "../utils";
import StatusBadge from "./StatusBadge";

/**
 * StatusFilterMenu - A reusable dropdown menu component for filtering by status
 * @param {function} onStatusChange - Callback when status is selected
 * @param {object} statusCountsData - API response with status counts
 * @param {boolean} loading - Loading state
 * @param {string} selectedStatus - Currently selected status
 * @param {string} buttonText - Text to show on the button when "all" is selected
 * @param {string} menuTitle - Title to show in the menu header
 * @param {string} variant - Button variant - default: "outlined"
 * @param {object} sx - Additional Material-UI sx props for the button
 */
const StatusFilterMenu = ({
  onStatusChange,
  statusCountsData = {},
  loading = false,
  selectedStatus = "all",
  buttonText = "All URLs",
  menuTitle = "Filter by Status",
  variant = "outlined",
  sx = {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Extract status counts from API response
  const statusCounts = statusCountsData?.status_counts || {};
  const totalCount = statusCountsData?.total_count || 0;

  // Build status options dynamically from API response
  const statusOptions = [
    {
      id: "all",
      label: buttonText,
      count: totalCount,
      color: "primary",
    },
    ...Object.entries(statusCounts).map(([statusId, count]) => ({
      id: statusId,
      label: convertUnderscoreToSpaces(statusId),
      count: count,
      color: getStatusColor(statusId),
    })),
  ];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusSelect = (statusId) => {
    onStatusChange(statusId);
    handleClose();
  };

  const getSelectedOption = () => {
    return (
      statusOptions.find((option) => option.id === selectedStatus) ||
      statusOptions[0]
    );
  };

  const selectedOption = getSelectedOption();

  return (
    <Box>
      <Button
        onClick={handleClick}
        disabled={loading}
        variant={variant}
        endIcon={<KeyboardArrowDown />}
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          minWidth: "160px",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          ...sx,
        }}
      >
        <StatusBadge
          status={selectedOption.id}
          count={selectedOption.count}
          showLabel={true}
          showCount={selectedOption.count > 0}
          variant="filled"
          size="small"
        />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: "280px",
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {menuTitle}
          </Typography>
        </Box>
        <Divider />

        {statusOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleStatusSelect(option.id)}
            selected={selectedStatus === option.id}
            sx={{
              px: 2,
              py: 1,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: selectedStatus === option.id ? 600 : 400,
                }}
              >
                {option.label}
              </Typography>
              <StatusBadge
                status={option.id}
                count={option.count}
                showLabel={false}
                showCount={option.count > 0}
                variant={selectedStatus === option.id ? "filled" : "outlined"}
                size="small"
              />
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default StatusFilterMenu;
